import { getLogger } from '@confluence/logger';

const logger = getLogger('retrieveContentExtensionMetrics');

export const MAX_DEPTH = 10;
export type ContentExtensionCount = {
	connect: Record<string, number>;
	forge: Record<string, number>;
};

export type ContentExtensionKeys = {
	connect: string[];
	forge: string[];
};

export type ContentExtensionDepth = {
	connect: number[];
	forge: number[];
};

type Node = {
	type?: any;
	attrs?: {
		extensionType?: string;
		extensionKey?: string;
	};
	content?: (Node | undefined)[];
};

export type ContentExtensionMetrics = {
	contentExtensionCount: ContentExtensionCount;
	contentExtensionKeys: ContentExtensionKeys;
	depth: ContentExtensionDepth;
	hasExtensions: boolean;
};

/* eslint-enable @atlassian/perf-linting/no-quadratic-complexity */

export const retrieveContentExtensionMetrics = (contentBody: string): {} => {
	try {
		const contentExtensionMetrics: ContentExtensionMetrics = {
			contentExtensionCount: {
				connect: {},
				forge: {},
			},
			contentExtensionKeys: {
				connect: [],
				forge: [],
			},
			depth: {
				connect: [],
				forge: [],
			},
			hasExtensions: false,
		};
		const document = JSON.parse(contentBody);

		const visit = (node: Node) => {
			retrieveContentExtensionMetricsFromNode(node, contentExtensionMetrics);
		};

		document?.content?.forEach(visit);

		return contentExtensionMetrics;
	} catch {
		logger.error`Unable to parse ADF`;
		return {};
	}
};

export const retrieveContentExtensionMetricsFromNode = (
	node: Node,
	contentExtensionMetrics: ContentExtensionMetrics,
	depth?: number,
): ContentExtensionMetrics => {
	retrieveContentExtensionCountFromNode(node, contentExtensionMetrics.contentExtensionCount);
	retrieveContentExtensionKeysFromNode(node, contentExtensionMetrics.contentExtensionKeys);
	if (depth !== undefined) {
		retrieveContentExtensionDepthFromNode(node, contentExtensionMetrics.depth, depth);
	}
	contentExtensionMetrics.hasExtensions =
		contentExtensionMetrics.contentExtensionKeys?.connect.length > 0 ||
		contentExtensionMetrics.contentExtensionKeys?.forge.length > 0;
	return contentExtensionMetrics;
};

function retrieveContentExtensionKeysFromNode(
	{ attrs, type }: Node,
	contentExtensionKeys: ContentExtensionKeys,
) {
	if (attrs && type) {
		const { extensionType, extensionKey } = attrs;
		if (extensionType === 'com.atlassian.ecosystem' && extensionKey) {
			contentExtensionKeys.forge.push(extensionKey);
		} else if (extensionType === 'com.atlassian.confluence.macro.core' && extensionKey) {
			contentExtensionKeys.connect.push(extensionKey);
		} else if (extensionType === 'com.atlassian.confluence.migration' && extensionKey) {
			contentExtensionKeys.connect.push(extensionKey);
		} else {
			return;
		}
	}
}

function retrieveContentExtensionCountFromNode(
	{ attrs, type }: Node,
	contentExtensionCount: ContentExtensionCount,
) {
	if (attrs && type) {
		const { extensionType } = attrs;

		let extensionsObj: undefined | ContentExtensionCount[keyof ContentExtensionCount];
		if (extensionType === 'com.atlassian.ecosystem') {
			extensionsObj = contentExtensionCount.forge;
		} else if (extensionType === 'com.atlassian.confluence.macro.core') {
			extensionsObj = contentExtensionCount.connect;
		} else if (extensionType === 'com.atlassian.confluence.migration') {
			extensionsObj = contentExtensionCount.connect;
		} else {
			return;
		}

		if (extensionsObj) {
			extensionsObj[type] = (extensionsObj[type] || 0) + 1;
		}
	}
}

function retrieveContentExtensionDepthFromNode(
	{ attrs, type }: Node,
	contentExtensionDepth: ContentExtensionDepth,
	depth: number,
) {
	if (attrs && type) {
		const { extensionType } = attrs;

		if (extensionType === 'com.atlassian.ecosystem') {
			contentExtensionDepth.forge.push(depth);
		} else if (
			extensionType === 'com.atlassian.confluence.macro.core' ||
			extensionType === 'com.atlassian.confluence.migration'
		) {
			contentExtensionDepth.connect.push(depth);
		}
	}
}
